"use client";

import Link from "next/link";
import { useCookieConsent } from "@/context/CookieConsentContext";

export default function CookiePolicyContent() {
    const { openManager } = useCookieConsent();

    return (
        <main className="max-w-3xl mx-auto px-6 py-20">
            <header className="mb-12">
                <h1 className="text-5xl font-extrabold text-gray-900 mb-2">
                    Cookie Policy
                </h1>
                <p className="text-sm text-gray-500">
                    Last updated: June 30, 2025
                </p>
            </header>

            <section className="space-y-10 text-base text-gray-800 leading-relaxed">
                <div>
                    <p>
                        This Cookie Policy explains how{" "}
                        <strong>MyBlog Times</strong> uses cookies and similar
                        technologies to recognize you when you visit our
                        website. It also explains what these technologies are
                        and why we use them, as well as your rights to control
                        our use of them.
                    </p>
                </div>

                <div>
                    <h2 className="text-2xl font-semibold mb-2 text-gray-900">
                        1. What Are Cookies?
                    </h2>
                    <p>
                        Cookies are small text files placed on your device when
                        you visit a website. They help us keep the site secure
                        and reliable, remember your preferences, and understand
                        how the site is used so we can improve it.
                    </p>
                </div>

                <div>
                    <h2 className="text-2xl font-semibold mb-2 text-gray-900">
                        2. Types of Cookies We Use
                    </h2>
                    <ul className="list-disc list-inside mt-2 space-y-1 text-gray-700">
                        <li>
                            <strong>Essential</strong> – Required for core
                            functionality like security, network management, and
                            accessibility. These cannot be switched off.
                        </li>
                        <li>
                            <strong>Analytics</strong> – Help us measure and
                            improve performance (e.g., which pages are most
                            popular).
                        </li>
                        <li>
                            <strong>Personalization</strong> – Tailor content
                            and recommendations to your interests.
                        </li>
                        <li>
                            <strong>Advertising</strong> – Enable us and
                            partners to show relevant ads and measure
                            effectiveness.
                        </li>
                        <li>
                            <strong>Preferences</strong> – Remember choices such
                            as language or theme.
                        </li>
                    </ul>
                </div>

                <div id="control">
                    <h2 className="text-2xl font-semibold mb-2 text-gray-900">
                        3. How You Can Control Cookies
                    </h2>
                    <p>
                        We only set non‑essential cookies with your consent. You
                        can manage your choices at any time using the button
                        below. You can also set or modify your browser settings
                        to refuse cookies or alert you when cookies are being
                        placed. Disabling certain cookies may affect site
                        features.
                    </p>
                </div>

                <div>
                    <h2 className="text-2xl font-semibold mb-2 text-gray-900">
                        4. Third‑Party Cookies
                    </h2>
                    <p>
                        Some cookies are set by third parties, such as analytics
                        providers or advertisers. These are subject to their own
                        privacy and cookie policies. We provide controls so you
                        can decide whether to enable these categories.
                    </p>
                </div>

                <div>
                    <h2 className="text-2xl font-semibold mb-2 text-gray-900">
                        5. Updates to This Policy
                    </h2>
                    <p>
                        We may update this Cookie Policy to reflect changes to
                        the cookies we use or for operational, legal, or
                        regulatory reasons. Please revisit this page regularly
                        to stay informed.
                    </p>
                </div>

                <div>
                    <h2 className="text-2xl font-semibold mb-2 text-gray-900">
                        6. More Information
                    </h2>
                    <p>
                        For details on how we process personal data, see our{" "}
                        <Link
                            href="/privacy-policy"
                            className="text-blue-600 hover:underline font-medium"
                        >
                            Privacy Policy
                        </Link>
                        . Questions?{" "}
                        <Link
                            href="/contact"
                            className="text-blue-600 hover:underline font-medium"
                        >
                            Contact us
                        </Link>
                        .
                    </p>
                </div>
            </section>

            <div className="mt-10 flex justify-center">
                <button
                    type="button"
                    onClick={() => openManager?.()}
                    className="px-6 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                    aria-describedby="cookie-manage-help"
                >
                    Manage Cookie Preferences
                </button>
            </div>
            <p
                id="cookie-manage-help"
                className="mt-3 text-center text-sm text-gray-500"
            >
                Opens a dialog where you can accept all cookies or customize
                Analytics, Personalization, Advertising, and Preferences.
            </p>
        </main>
    );
}
